/**
 * WizardStepUpload Component
 *
 * Step 1 of the presentation wizard: File selection and validation.
 * Allows drag & drop or click to select video file.
 */

import React, { useState, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    LinearProgress,
    Alert,
    Tabs,
    Tab
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    VideoLibrary as VideoIcon,
    CheckCircle as CheckIcon,
    VideoCall as ZoomIcon
} from '@mui/icons-material';
import ZoomRecordingSelector from '../../zoom/ZoomRecordingSelector';

interface WizardStepUploadProps {
    selectedFile: File | null;
    onFileSelect: (file: File) => void;
    onNext: () => void;
    onZoomRecordingSelected?: (presentationId: string, recording: any) => void;
}

const WizardStepUpload: React.FC<WizardStepUploadProps> = ({ selectedFile, onFileSelect, onNext, onZoomRecordingSelected }) => {
    const [dragActive, setDragActive] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [uploadMode, setUploadMode] = useState<'file' | 'zoom'>('file');
    const [zoomPresentationId, setZoomPresentationId] = useState<string | null>(null);

    // Supported video formats
    const SUPPORTED_FORMATS = [
        'video/mp4',
        'video/quicktime',
        'video/x-msvideo',
        'video/x-matroska',
        'video/webm'
    ];

    const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB

    // Validate file
    const validateFile = (file: File) => {
        setError(null);

        if (!file) {
            setError('No file selected');
            return false;
        }

        // Check file type
        if (!SUPPORTED_FORMATS.includes(file.type)) {
            setError(`Unsupported file format. Please upload: MP4, MOV, AVI, MKV, or WebM`);
            return false;
        }

        // Check file size
        if (file.size > MAX_FILE_SIZE) {
            setError(`File too large. Maximum size is 500MB. Your file is ${(file.size / 1024 / 1024).toFixed(1)}MB`);
            return false;
        }

        return true;
    };

    // Handle file selection
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && validateFile(file)) {
            onFileSelect(file);
        }
    };

    // Handle drag events
    const handleDrag = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    }, []);

    // Handle drop
    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            if (validateFile(file)) {
                onFileSelect(file);
            }
        }
    }, [onFileSelect]);

    // Format file size
    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / 1024 / 1024).toFixed(1) + ' MB';
    };

    const handleZoomImportComplete = (presentationId: string, recording: any) => {
        setZoomPresentationId(presentationId);
        if (onZoomRecordingSelected) {
            onZoomRecordingSelected(presentationId, recording);
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: 'file' | 'zoom') => {
        setUploadMode(newValue);
        setError(null);
    };

    const canProceed = uploadMode === 'file'
        ? selectedFile && !error
        : zoomPresentationId !== null;

    return (
        <Box sx={{ width: '100%' }}>
            <Typography variant="h6" gutterBottom>
                Select Presentation Video
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                Upload a video file or import from Zoom
            </Typography>

            {/* Upload Mode Tabs */}
            <Tabs value={uploadMode} onChange={handleTabChange} sx={{ mb: 3 }}>
                <Tab icon={<UploadIcon />} label="Upload File" value="file" />
                <Tab icon={<ZoomIcon />} label="Import from Zoom" value="zoom" />
            </Tabs>

            {/* Content based on selected tab */}
            {uploadMode === 'file' ? (
                <>
                    {/* Drag & Drop Zone */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            textAlign: 'center',
                            border: '2px dashed',
                            borderColor: dragActive ? 'primary.main' : selectedFile ? 'success.main' : 'grey.300',
                            bgcolor: dragActive ? 'action.hover' : selectedFile ? 'success.light' : 'background.paper',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                borderColor: selectedFile ? 'success.main' : 'primary.main',
                                bgcolor: 'action.hover'
                            }
                        }}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={() => document.getElementById('file-input')?.click()}
                    >
                        <input
                            id="file-input"
                            type="file"
                            accept="video/*"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />

                        {!selectedFile ? (
                            <>
                                <UploadIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="h6" gutterBottom>
                                    Drag & Drop Video Here
                                </Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    or click to browse files
                                </Typography>
                                <Button variant="outlined" startIcon={<VideoIcon />}>
                                    Choose File
                                </Button>
                            </>
                        ) : (
                            <>
                                <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                                <Typography variant="h6" gutterBottom>
                                    {selectedFile.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Size: {formatFileSize(selectedFile.size)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Type: {selectedFile.type || 'Unknown'}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    startIcon={<VideoIcon />}
                                    sx={{ mt: 2 }}
                                >
                                    Choose Different File
                                </Button>
                            </>
                        )}
                    </Paper>

                    {/* Error Message */}
                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {/* File Info */}
                    {selectedFile && !error && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                            File selected successfully. Click "Next" to continue.
                        </Alert>
                    )}

                </>
            ) : (
                <ZoomRecordingSelector
                    onImportComplete={handleZoomImportComplete}
                />
            )}

            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                    variant="contained"
                    onClick={onNext}
                    disabled={!canProceed}
                    size="large"
                >
                    {uploadMode === 'file'
                        ? 'Next: Presentation Details'
                        : zoomPresentationId
                            ? 'Continue to Presentation Details'
                            : 'Select a Recording First'}
                </Button>
            </Box>
        </Box>
    );
};

export default WizardStepUpload;
